import React, { useState, useEffect, useCallback } from "react";
import { MDBIcon } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { api } from "../services";
import RightDrawer from "./RightDrawer";
import { formatTrackHubError } from "../utils/formatTrackHubError";
import "./order-drawer.css";

const OrderTimeline = ({ show, onClose, orderDetails, embedded = false, onLoadingChange }) => {
  const { t, i18n } = useTranslation("translation");
  const [orderLogs, setOrderLogs] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const translateStatusTagline = (tagline, _status) => {
    const s = String(tagline || "").trim();
    if (!s) return "";
    const localizeEntityName = (name) => {
      const v = String(name || "").trim().replace(/\.$/, "");
      const l = v.toLowerCase();
      const platformKeys = {
        jahez: "platformName.JAHEZ",
        keeta: "platformName.KEETA",
        ninja: "platformName.NINJA",
        "the chefz": "platformName.THE_CHEFZ",
        "ninja  - staging": "platformName.NINJA_STAGING",
        "ninja - staging": "platformName.NINJA_STAGING",
        "chat agent": "platformName.CHAT_AGENT",
        "order agent": "platformName.CHAT_AGENT",
        "digital catalog": "platformName.DIGITAL_CATALOG",
        digital_catalog: "platformName.DIGITAL_CATALOG",
        digitalcatalog: "platformName.DIGITAL_CATALOG",
        chat_agent: "platformName.CHAT_AGENT",
        track_hub: "platformName.TRACK_HUB",
        trackhub: "platformName.TRACK_HUB",
        mrsool: "platformName.MRSOOL",
      };
      if (platformKeys[l]) return t(platformKeys[l]);
      if (l === "merchant") return t("actor.merchant");
      if (l === "restaurant") return t("actor.restaurant");
      if (i18n.dir() === "rtl") {
        if (["delivery courier", "courier", "delivery driver", "driver"].includes(l))
          return "المندوب";
        if (["delivery service"].includes(l)) return t("actor.delivery_service");
      }
      return v;
    };
    const patterns = [
      {
        regex: /^Order received by the restaurant$/i,
        key: "statusTagline.RECEIVED_RESTAURANT",
      },
      {
        regex: /^Order received from\s+(.+)\.?$/i,
        key: "statusTagline.RECEIVED_FROM_PLATFORM",
        vars: (m) => ({ platform: localizeEntityName(m[1]) }),
      },
      {
        regex: /^Order accepted by logged-?in user or restaurant$/i,
        key: "statusTagline.ACCEPTED_USER_OR_RESTAURANT",
      },
      {
        regex: /^Order accepted by\s+(.+)\.?$/i,
        key: "statusTagline.ACCEPTED_BY_USER",
        vars: (m) => ({ user: localizeEntityName(m[1]) }),
      },
      {
        regex: /^Order ready by logged-?in user(.*)$/i,
        key: "statusTagline.READY_OR_READY_FOR_DELIVERY",
      },
      {
        regex: /^Order ready by\s+(.+)\.?$/i,
        key: "statusTagline.READY_BY_USER",
        vars: (m) => ({ user: localizeEntityName(m[1]) }),
      },
      {
        regex: /^(Order\s)?ready for delivery$/i,
        key: "statusTagline.READY_OR_READY_FOR_DELIVERY",
      },
      { regex: /^Driver assigned to the order\.?$/i, key: "statusTagline.DRIVER_ASSIGNED" },
      {
        regex: /^Driver assigned to order (\d+) \(([^)]+)\)$/i,
        key: "reason.webhook.driver_assigned_to_order",
        vars: (m) => ({ orderId: m[1], platform: localizeEntityName(m[2]) }),
      },
      {
        regex: /^Driver assigned \(([^)]+)\)$/i,
        key: "reason.webhook.driver_assigned",
        vars: (m) => ({ platform: localizeEntityName(m[1]) }),
      },
      {
        regex: /^Order delivered success(?:fully|full)\.?$/i,
        key: "statusTagline.DELIVERED_SUCCESS",
      },
      {
        regex: /^Order cancelled by customer$/i,
        key: "statusTagline.CANCELLED_BY_CUSTOMER",
      },
      {
        regex: /^Customer cancel(?:led|ed) the order\.?$/i,
        key: "statusTagline.CANCELLED_BY_CUSTOMER",
      },
      {
        regex: /^Order cancelled by\s+(.+)\.?$/i,
        key: "statusTagline.CANCELLED_BY_USER",
        vars: (m) => ({ user: localizeEntityName(m[1]) }),
      },
      {
        regex: /^Order rejected by logged-?in user or restaurant$/i,
        key: "statusTagline.REJECTED_USER_OR_RESTAURANT",
      },
      {
        regex: /^Order rejected by (?:logged-?in )?user or restaurant\.?$/i,
        key: "statusTagline.REJECTED_USER_OR_RESTAURANT",
      },
      {
        regex: /^Order rejected by\s+(.+)\.?$/i,
        key: "statusTagline.REJECTED_BY_USER",
        vars: (m) => ({ user: localizeEntityName(m[1]) }),
      },
      {
        regex: /^Order timed out by system due to no response$/i,
        key: "statusTagline.TIMED_OUT_SYSTEM",
      },
      {
        regex: /^Order timed out due to no response\s*\??\s*$/i,
        key: "statusTagline.TIMED_OUT_NO_RESPONSE",
      },
      {
        regex: /^System unable to process the order action$/i,
        key: "statusTagline.SYSTEM_UNABLE_TO_PROCESS",
      },
      {
        regex: /^Order accepted by delivery courier$/i,
        key: "statusTagline.ACCEPTED_BY_COURIER",
      },
      { regex: /^Order has been processed$/i, key: "statusTagline.PROCESSED" },
      {
        regex: /^TrackHub shipment created$/i,
        key: "statusTagline.SHIPMENT_CREATED",
      },
      {
        regex: /^Shipment created for this order$/i,
        key: "statusTagline.SHIPMENT_CREATED",
      },
      {
        regex: /^Courier assigned to the shipment$/i,
        key: "statusTagline.SHIPMENT_ASSIGNED",
      },
      {
        regex: /^Courier arrived at pickup location$/i,
        key: "statusTagline.SHIPMENT_AT_PICKUP",
      },
      {
        regex: /^Courier picked up the order$/i,
        key: "statusTagline.SHIPMENT_PICKED_UP",
      },
      {
        regex: /^Shipment is on the way to the customer$/i,
        key: "statusTagline.SHIPMENT_IN_TRANSIT",
      },
      {
        regex: /^Courier arrived at dropoff location$/i,
        key: "statusTagline.SHIPMENT_AT_DROPOFF",
      },
      {
        regex: /^Shipment delivered successfully$/i,
        key: "statusTagline.SHIPMENT_DELIVERED",
      },
      {
        regex: /^TrackHub shipment failed$/i,
        key: "statusTagline.SHIPMENT_FAILED",
      },
      {
        regex: /^Shipment could not be completed$/i,
        key: "statusTagline.SHIPMENT_FAILED",
      },
      {
        regex: /^Shipment was returned$/i,
        key: "statusTagline.SHIPMENT_RETURNED",
      },
      {
        regex: /^TrackHub shipment cancelled$/i,
        key: "statusTagline.SHIPMENT_CANCELLED",
      },
      {
        regex: /^Shipment was cancelled$/i,
        key: "statusTagline.SHIPMENT_CANCELLED",
      },
    ];
    for (const p of patterns) {
      const m = s.match(p.regex);
      if (m) {
        const vars = p.vars ? p.vars(m) : undefined;
        return t(p.key, vars);
      }
    }
    // Hide bare TrackHub status enums that leaked into tagline (e.g. IN_TRANSIT)
    if (/^(SHIPMENT_)?[A-Z][A-Z0-9_]{2,}$/.test(s) && String(_status || "").startsWith("SHIPMENT_")) {
      const fallbackKey = `statusTagline.${String(_status).toUpperCase()}`;
      const fallback = t(fallbackKey, "");
      if (fallback && fallback !== fallbackKey) return fallback;
      return "";
    }
    const r = translateReason(s);
    if (r && r !== s) return r;
    return s;
  };

  const isTrackHubTechnicalReason = (reason) => {
    if (!reason) return true;
    const text = String(reason).trim();
    if (!text || text === "N/A") return true;
    if (/^trackhub\s+(webhook|status)\s*:/i.test(text)) return true;
    if (/^trackhub\s+shipment\b/i.test(text)) return true;
    if (/^Order status updated to\s+(undefined|null)\b/i.test(text)) return true;
    if (/^(SHIPMENT_)?[A-Z][A-Z0-9_]{2,}$/.test(text)) return true;
    return false;
  };

  const translateReason = (reason) => {
    if (!reason) return "";
    if (isTrackHubTechnicalReason(reason)) return "";

    const translateReasonText = (reasonText) => {
      if (!reasonText) return "";
      const reasonLower = String(reasonText)
        .toLowerCase()
        .trim()
        .replace(/^\.+|\.$/, "");
      const reasonTextMap = {
        "customer canceled the order": "reasonText.customer_canceled_the_order",
        "customer rejected the order": "reasonText.customer_rejected_the_order",
        "out of stock": "reasonText.out_of_stock",
        "item not available": "reasonText.item_not_available",
        "store closed": "reasonText.store_closed",
        "preparation time exceeded": "reasonText.preparation_time_exceeded",
        "restaurant did not respond in time": "reasonText.restaurant_did_not_respond",
        "order rejected by merchant": "reasonText.order_rejected_by_merchant",
        "order cancelled by merchant": "reasonText.order_cancelled_by_merchant",
        "order canceled by merchant": "reasonText.order_cancelled_by_merchant",
        "order rejected by restaurant": "reasonText.order_rejected_by_restaurant",
        "order cancelled by restaurant": "reasonText.order_cancelled_by_restaurant",
        "order canceled by restaurant": "reasonText.order_cancelled_by_restaurant",
      };
      const key = reasonTextMap[reasonLower];
      const translated = key ? t(key) : reasonText;
      return translated;
    };

    const normalizePlatform = (name) => {
      const v = String(name || "").trim().replace(/\.$/, "");
      const l = v.toLowerCase();
      if (l === "merchant") return t("actor.merchant");
      if (l === "restaurant") return t("actor.restaurant");
      const platformKeys = {
        jahez: "platformName.JAHEZ",
        keeta: "platformName.KEETA",
        ninja: "platformName.NINJA",
        "the chefz": "platformName.THE_CHEFZ",
        "ninja  - staging": "platformName.NINJA_STAGING",
        "ninja - staging": "platformName.NINJA_STAGING",
        "chat agent": "platformName.CHAT_AGENT",
        "order agent": "platformName.CHAT_AGENT",
        "digital catalog": "platformName.DIGITAL_CATALOG",
        digital_catalog: "platformName.DIGITAL_CATALOG",
        digitalcatalog: "platformName.DIGITAL_CATALOG",
        chat_agent: "platformName.CHAT_AGENT",
        track_hub: "platformName.TRACK_HUB",
        trackhub: "platformName.TRACK_HUB",
        mrsool: "platformName.MRSOOL",
      };
      const key = platformKeys[l];
      return key ? t(key) : v;
    };
    const patterns = [
      {
        regex: /^Customer cancel(?:led|ed) the order\.?$/i,
        key: "reason.customer.cancelled",
      },
      { regex: /^Order cancelled by customer\.?$/i, key: "reason.customer.cancelled" },
      {
        regex: /^Customer requests order cancellation\.?$/i,
        key: "reasonText.customer_requests_cancellation",
      },
      {
        regex: /^Restaurant did not respond in time\.?$/i,
        key: "reasonText.restaurant_did_not_respond",
      },
      { regex: /^Order rejected by merchant\.?$/i, key: "reasonText.order_rejected_by_merchant" },
      { regex: /^Order cancel(?:led|ed) by merchant\.?$/i, key: "reasonText.order_cancelled_by_merchant" },
      { regex: /^Order rejected by restaurant\.?$/i, key: "reasonText.order_rejected_by_restaurant" },
      { regex: /^Order cancel(?:led|ed) by restaurant\.?$/i, key: "reasonText.order_cancelled_by_restaurant" },
      { regex: /^Out of stock\.?$/i, key: "reasonText.out_of_stock" },
      { regex: /^Item not available\.?$/i, key: "reasonText.item_not_available" },
      { regex: /^Store closed\.?$/i, key: "reasonText.store_closed" },
      {
        regex: /^Preparation time exceeded\.?$/i,
        key: "reasonText.preparation_time_exceeded",
      },
      {
        regex: /^Order created via Digital Catalog\.?$/i,
        key: "reasonText.order_created_via_digital_catalog",
      },
      {
        regex: /^Order created via Order Agent\.?$/i,
        key: "reasonText.order_created_via_order_agent",
      },
      {
        regex: /^Order created via (Chat Agent|AI Order Agent)\.?$/i,
        key: "reasonText.order_created_via_chat_agent",
      },
      { regex: /^Order has been rejected by user$/i, key: "reason.user.rejected" },
      { regex: /^Order has been cancelled by user$/i, key: "reason.user.cancelled" },
      { regex: /^Order has been accepted by user$/i, key: "reason.user.accepted" },
      {
        regex: /^Order has been marked as ready for delivery by user$/i,
        key: "reason.user.ready_for_delivery",
      },
      {
        regex: /^Order has been marked as delivered by user$/i,
        key: "reason.user.delivered",
      },
      {
        regex: /^Order status updated to (.+) by user$/i,
        key: "reason.user.status_updated",
        vars: (m) => ({ status: m[1] }),
      },

      {
        regex: /^Order received from\s+([^:]+)(?::\s+(.+))?$/i,
        key: "reason.webhook.received_from_platform",
        vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
      },
      {
        regex: /^Order has been rejected by ([^:]+)(?::\s+(.+))?$/i,
        key: "reason.webhook.rejected",
        vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
      },
      {
        regex: /^Order has been cancelled by ([^:]+)(?::\s+(.+))?$/i,
        key: "reason.webhook.cancelled",
        vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
      },
      {
        regex: /^Order has been accepted by ([^:]+)(?::\s+(.+))?$/i,
        key: "reason.webhook.accepted",
        vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
      },
      {
        regex: /^Order has been marked as ready for delivery by ([^:]+)(?::\s+(.+))?$/i,
        key: "reason.webhook.ready_for_delivery",
        vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
      },
      {
        regex: /^Order is out for delivery \(([^)]+)\)(?::\s+(.+))?$/i,
        key: "reason.webhook.out_for_delivery",
        vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
      },
      {
        regex: /^Driver assigned to order (\d+) \(([^)]+)\)$/i,
        key: "reason.webhook.driver_assigned_to_order",
        vars: (m) => ({ orderId: m[1], platform: normalizePlatform(m[2]) }),
      },
      {
        regex: /^Driver assigned \(([^)]+)\)$/i,
        key: "reason.webhook.driver_assigned",
        vars: (m) => ({ platform: normalizePlatform(m[1]) }),
      },
      {
        regex: /^Order has been delivered \(([^)]+)\)(?::\s+(.+))?$/i,
        key: "reason.webhook.delivered",
        vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
      },
      {
        regex: /^Order timed out \(([^)]+)\)(?::\s+(.+))?$/i,
        key: "reason.webhook.timed_out",
        vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
      },
      {
        regex: /^Order status updated to (.+) by ([^:]+)(?::\s+(.+))?$/i,
        key: "reason.webhook.status_updated",
        vars: (m) => ({ status: m[1], platform: normalizePlatform(m[2]), reason: m[3] || "" }),
      },

      {
        regex: /^Order has been automatically accepted by system$/i,
        key: "reason.system.auto_accepted",
      },
      {
        regex: /^Order has been automatically timed out due to no response$/i,
        key: "reason.system.auto_timed_out",
      },
      {
        regex: /^Order timed out due to no response\s*\??\s*$/i,
        key: "reason.system.timed_out_no_response",
      },
      {
        regex: /^Order Timed Out by System, not responding to it\.?$/i,
        key: "reason.system.auto_timed_out",
      },
      { regex: /^Auto accepted by system$/i, key: "reason.system.auto_accepted" },
      { regex: /^Auto time-out due to no response$/i, key: "reason.system.auto_timed_out" },
      { regex: /^Order has been rejected$/i, key: "reason.system.rejected" },
      { regex: /^Order has been cancelled$/i, key: "reason.system.cancelled" },
      { regex: /^Order has been accepted$/i, key: "reason.system.accepted" },
      {
        regex: /^Order status updated to (.+)$/i,
        key: "reason.system.status_updated",
        vars: (m) => ({ status: m[1] }),
      },
    ];
    for (const p of patterns) {
      const m = reason.match(p.regex);
      if (m) {
        const vars = p.vars ? p.vars(m) : undefined;
        // Translate reason text if present
        if (vars?.reason && String(vars.reason).trim()) {
          vars.reason = translateReasonText(vars.reason);
        }
        // Use _with_reason key if reason text exists, otherwise use the base key
        const keyToUse =
          vars?.reason && String(vars.reason).trim() ? `${p.key}_with_reason` : p.key;
        const translated = t(keyToUse, vars);
        return translated;
      }
    }
    // Fallback: try to translate as generic reason text
    const translated = translateReasonText(reason);
    return translated;
  };

  const mapLogsToTimeline = (logs) => {
    if (!logs || !Array.isArray(logs)) return [];

    // Map status to display text and icons
    const statusMap = {
      PENDING: { text: t("statusText.PENDING"), icon: "check", type: "success" },
      ACCEPTED: { text: t("statusText.ACCEPTED"), icon: "file-circle-check", type: "success" },
      "READY FOR DELIVERY": {
        text: t("statusText.READY FOR DELIVERY"),
        icon: "check",
        type: "success",
      },
      "OUT FOR DELIVERY": {
        text: t("statusText.OUT FOR DELIVERY"),
        icon: "truck",
        type: "info",
      },
      DELIVERED: { text: t("statusText.DELIVERED"), icon: "check-circle", type: "success" },
      REJECTED: { text: t("statusText.REJECTED"), icon: "times", type: "error" },
      CANCELLED: { text: t("statusText.CANCELLED"), icon: "times", type: "error" },
      "TIME OUT": { text: t("statusText.TIME OUT"), icon: "clock", type: "timeout" },
      "RIDER ASSIGNED": {
        text: t("statusText.RIDER ASSIGNED"),
        icon: "check",
        type: "success",
      },
      "ACCEPTED BY COURIER": {
        text: t("statusText.ACCEPTED BY COURIER"),
        icon: "check",
        type: "success",
      },
      SHIPMENT_CREATED: {
        text: t("statusText.SHIPMENT_CREATED", "Shipment Created"),
        icon: "truck",
        type: "info",
      },
      SHIPMENT_ASSIGNED: {
        text: t("statusText.SHIPMENT_ASSIGNED", "Courier Assigned"),
        icon: "truck",
        type: "info",
      },
      SHIPMENT_AT_PICKUP: {
        text: t("statusText.SHIPMENT_AT_PICKUP", "Courier at Pickup"),
        icon: "truck",
        type: "info",
      },
      SHIPMENT_PICKED_UP: {
        text: t("statusText.SHIPMENT_PICKED_UP", "Shipment Picked Up"),
        icon: "truck",
        type: "info",
      },
      SHIPMENT_IN_TRANSIT: {
        text: t("statusText.SHIPMENT_IN_TRANSIT", "Shipment In Transit"),
        icon: "truck",
        type: "info",
      },
      SHIPMENT_AT_DROPOFF: {
        text: t("statusText.SHIPMENT_AT_DROPOFF", "Courier at Dropoff"),
        icon: "truck",
        type: "info",
      },
      SHIPMENT_DELIVERED: {
        text: t("statusText.SHIPMENT_DELIVERED", "Shipment Delivered"),
        icon: "check-circle",
        type: "success",
      },
      SHIPMENT_FAILED: {
        text: t("statusText.SHIPMENT_FAILED", "Shipment Failed"),
        icon: "times",
        type: "error",
      },
      SHIPMENT_RETURNED: {
        text: t("statusText.SHIPMENT_RETURNED", "Shipment Returned"),
        icon: "times",
        type: "error",
      },
      SHIPMENT_CANCELLED: {
        text: t("statusText.SHIPMENT_CANCELLED", "Shipment Cancelled"),
        icon: "times",
        type: "error",
      },
    };

    return logs
      .map((log) => {
        const statusInfo = statusMap[log.status] || {
          text: log.status,
          icon: "info",
          type: "info",
        };
        const statusKey = String(log.status || "");
        // Prefer i18n by status for shipment events so AR/EN always resolve
        // even when stored taglines are English technical leftovers.
        let tagline = translateStatusTagline(log.status_tagline, log.status);
        if (statusKey.startsWith("SHIPMENT_")) {
          const byStatus = t(`statusTagline.${statusKey}`, "");
          if (byStatus && byStatus !== `statusTagline.${statusKey}`) {
            tagline = byStatus;
          }
        }
        return {
          id: log._id,
          datetime: new Date(log.datetime),
          status: log.status,
          displayText: statusInfo.text,
          icon: statusInfo.icon,
          type: statusInfo.type,
          platform: log.platform,
          user: tagline,
          reason: translateReason(log.platform_status_reason),
          performedBy: log.user?.email || "System",
        };
      })
      .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
  };

  const mergeTrackHubEvents = (events, trackhub) => {
    if (!trackhub) return events;

    const hasShipmentLog = (events || []).some((e) =>
      String(e.status || "").startsWith("SHIPMENT_")
    );
    if (hasShipmentLog) return events;

    const extras = [];
    const createdAt = trackhub.createdAt
      ? new Date(trackhub.createdAt)
      : trackhub.lastSyncedAt
        ? new Date(trackhub.lastSyncedAt)
        : null;

    if (trackhub.shipmentId) {
      extras.push({
        id: `trackhub-created-${trackhub.shipmentId}`,
        datetime: createdAt || new Date(),
        status: "SHIPMENT_CREATED",
        displayText: t("statusText.SHIPMENT_CREATED", "Shipment Created"),
        icon: "truck",
        type: "info",
        platform: "TRACK_HUB",
        user: t(
          "statusTagline.SHIPMENT_CREATED",
          "Shipment created for this order"
        ),
        reason: "",
        performedBy: "System",
      });
    }

    if (trackhub.lastError || trackhub.lastErrorMessage) {
      extras.push({
        id: `trackhub-failed-${orderDetails?._id || "order"}`,
        datetime: trackhub.lastSyncedAt
          ? new Date(trackhub.lastSyncedAt)
          : createdAt || new Date(),
        status: "SHIPMENT_FAILED",
        displayText: t("statusText.SHIPMENT_FAILED", "Shipment Failed"),
        icon: "times",
        type: "error",
        platform: "TRACK_HUB",
        user: t(
          "statusTagline.SHIPMENT_FAILED",
          "Shipment could not be completed"
        ),
        reason: formatTrackHubError(trackhub, { t, isRTL }),
        performedBy: "System",
      });
    } else if (trackhub.status && trackhub.shipmentId) {
      const statusKey = `SHIPMENT_${String(trackhub.status).toUpperCase()}`;
      const mapped = {
        CREATED: "SHIPMENT_CREATED",
        ASSIGNED: "SHIPMENT_ASSIGNED",
        AT_PICKUP: "SHIPMENT_AT_PICKUP",
        PICKED_UP: "SHIPMENT_PICKED_UP",
        IN_TRANSIT: "SHIPMENT_IN_TRANSIT",
        AT_DROPOFF: "SHIPMENT_AT_DROPOFF",
        DELIVERED: "SHIPMENT_DELIVERED",
        FAILED: "SHIPMENT_FAILED",
        RETURNED: "SHIPMENT_RETURNED",
      };
      const status =
        mapped[String(trackhub.status).toUpperCase()] || statusKey;
      if (status !== "SHIPMENT_CREATED") {
        extras.push({
          id: `trackhub-status-${trackhub.shipmentId}-${status}`,
          datetime: trackhub.lastSyncedAt
            ? new Date(trackhub.lastSyncedAt)
            : createdAt || new Date(),
          status,
          displayText: t(`statusText.${status}`, trackhub.status),
          icon: status === "SHIPMENT_DELIVERED" ? "check-circle" : "truck",
          type:
            status === "SHIPMENT_FAILED" || status === "SHIPMENT_RETURNED"
              ? "error"
              : status === "SHIPMENT_DELIVERED"
                ? "success"
                : "info",
          platform: "TRACK_HUB",
          user: t(`statusTagline.${status}`, ""),
          reason: "",
          performedBy: "System",
        });
      }
    }

    return [...(events || []), ...extras].sort(
      (a, b) => new Date(a.datetime) - new Date(b.datetime)
    );
  };

  const fetchOrderLogs = useCallback(async () => {
    if (!orderDetails?._id) {
      setError(t("Order ID not available"));
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");

      const response = await api.get(`/order/${orderDetails._id}/logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderLogs(response.data.logs || []);
      setError("");
    } catch (err) {
      setError(err.response?.data.message || "Failed to fetch order logs");
      console.error("Failed to fetch order logs:", err);
    } finally {
      setIsLoading(false);
    }
  }, [orderDetails?._id]);

  // Fetch logs when modal is opened or when embedded and orderDetails is available
  useEffect(() => {
    if ((show || embedded) && orderDetails) {
      fetchOrderLogs();
    }
  }, [show, embedded, orderDetails, fetchOrderLogs]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  if (!orderDetails) return null;

  const isRTL = i18n.language === "ar" || i18n.dir() === "rtl";
  const events = mergeTrackHubEvents(
    mapLogsToTimeline(orderLogs),
    orderDetails?.trackhub
  );
  const dateLabel = events[0]?.datetime.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const eventDotClass = (event) => {
    if (event.type === "error") return "oh-order-timeline__dot--error";
    if (event.type === "timeout") return "oh-order-timeline__dot--timeout";
    if (event.status === "DELIVERED" || event.status === "SHIPMENT_DELIVERED") {
      return "oh-order-timeline__dot--delivered";
    }
    return "oh-order-timeline__dot--default";
  };

  const renderEventIcon = (event) => {
    if (event.status === "ACCEPTED") {
      return (
        <svg
          className="oh-order-timeline__accepted-icon"
          width="20"
          height="20"
          viewBox="0 0 44 44"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M23 32H16C14.8954 32 14 31.1046 14 30V14C14 12.8954 14.8954 12 16 12H28C29.1046 12 30 12.8954 30 14V22"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M19 18H25" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M19 23H25" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M19 28H21" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
          <path
            d="M26 29L29 32L35 20"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    if (event.status === "DELIVERED" || event.status === "SHIPMENT_DELIVERED") {
      return (
        <svg
          className="oh-order-timeline__delivered-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <circle cx="12" cy="12" r="9" stroke="#12B76A" strokeWidth="2" />
          <path
            d="M8 12.5L10.5 15L16 9.5"
            stroke="#12B76A"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    return <MDBIcon icon={event.icon} size="sm" />;
  };

  const renderEventRow = (event, isLastItem) => {
    const time = event.datetime.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const reason = String(event.reason || "").trim();
    const isShipmentLifecycle =
      String(event.status || "").startsWith("SHIPMENT_") &&
      !["SHIPMENT_FAILED", "SHIPMENT_RETURNED", "SHIPMENT_CANCELLED"].includes(
        event.status
      );
    const showReason = Boolean(
      reason &&
        !isShipmentLifecycle &&
        (!isRTL ||
          ["CANCELLED", "REJECTED", "SHIPMENT_FAILED", "SHIPMENT_RETURNED", "SHIPMENT_CANCELLED"].includes(
            event.status
          ))
    );

    return (
      <div
        key={event.id}
        className={`oh-order-timeline__row${isRTL ? " oh-order-timeline__row--rtl" : ""}`}
      >
        <div className="oh-order-timeline__time">{time}</div>
        <div className="oh-order-timeline__rail">
          <div
            className={`oh-order-timeline__line${
              isLastItem ? " oh-order-timeline__line--last" : ""
            }`}
          />
          <div className={`oh-order-timeline__dot ${eventDotClass(event)}`}>
            {renderEventIcon(event)}
          </div>
        </div>
        <div className="oh-order-timeline__card">
          <div className="oh-order-timeline__card-title">{event.displayText}</div>
          <div className="oh-order-timeline__card-meta">
            <span>{event.user || "N/A"}</span>
            {showReason ? (
              <span>
                {t("reason")}: {reason}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  // Timeline content (can be embedded or wrapped in RightDrawer) — Figma 1:3849
  const timelineContent = (
    <div
      className={
        embedded
          ? "oh-order-drawer-stack oh-order-drawer-stack--timeline"
          : "oh-order-drawer-stack oh-order-drawer-stack--timeline oh-order-timeline--standalone"
      }
    >
      <div className="oh-order-drawer-section-title oh-order-drawer-section-title--timeline">
        {t("timeline")}
      </div>

      {isLoading && !embedded ? (
        <div className="oh-order-timeline__state">
          <MDBIcon icon="circle-notch" spin size="2x" className="mb-3" />
          <p>{t("Loading timeline...")}</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger mb-0">
          <MDBIcon icon="exclamation-circle" className="me-2" />
          {error}
        </div>
      ) : (
        <div className={`oh-order-timeline${isRTL ? " oh-order-timeline--rtl" : ""}`}>
          <div
            className={`oh-order-timeline__row oh-order-timeline__row--date${
              isRTL ? " oh-order-timeline__row--rtl" : ""
            }`}
          >
            <div className="oh-order-timeline__time" aria-hidden="true" />
            <div className="oh-order-timeline__rail oh-order-timeline__rail--date">
              <div className="oh-order-timeline__date-pill">{dateLabel}</div>
              <div className="oh-order-timeline__line oh-order-timeline__line--from-date" />
            </div>
            <div aria-hidden="true" />
          </div>
          {events.map((event, index, array) =>
            renderEventRow(event, index === array.length - 1)
          )}
        </div>
      )}
    </div>
  );

  // If embedded, return content directly. Otherwise wrap in RightDrawer
  if (embedded) {
    return timelineContent;
  }

  return (
    <RightDrawer
      open={show}
      onClose={onClose}
      title={t("ordercycle")}
      width="559px"
      placement={isRTL ? "left" : "right"}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {timelineContent}
    </RightDrawer>
  );
};

export default OrderTimeline;
